// isomorphic-dompurify: real DOMPurify in the browser, jsdom-backed in test
// environments where happy-dom's DOM is unsupported (plain `dompurify` silently
// returns its input unsanitized there).
import DOMPurify from "isomorphic-dompurify";
import { marked } from "marked";

/**
 * Content rendering + difference highlighting for the conflict review modal
 * (design handoff `design_handoff_conflict_review_1a`, option 1A).
 *
 * The diff runs on the *text projection* of each rendered version, never on raw
 * source — diffing raw markup is what produced the unreadable tag-soup output
 * the redesign replaces. Changed runs are then mapped back onto the rendered
 * HTML by walking text nodes, so a `##` heading and an `<h2>` heading diff the
 * same way they render: as text.
 */

/** Tags the review panes may render; everything else is stripped. */
const ALLOWED_TAGS = [
    "h1", "h2", "h3", "h4", "p", "strong", "em", "b", "i", "ul", "ol", "li", "a",
    "code", "pre", "br", "hr", "table", "thead", "tbody", "tr", "th", "td",
    "blockquote", "del", "input", "span", "div",
];

/** Beyond this size the diff is skipped and panes render unmarked (§6). */
export const DIFF_CHAR_BUDGET = 40_000;
const DIFF_TOKEN_BUDGET = 2_000;

export function sanitizeHtml(raw: string): string {
    return DOMPurify.sanitize(raw, {
        ALLOWED_TAGS,
        ALLOWED_ATTR: ["href", "type", "checked", "disabled"],
        ALLOW_DATA_ATTR: false,
    });
}

/** Heuristic for content with no declared format: markup renders, prose wraps. */
export function looksLikeHtml(value: string): boolean {
    return /<([a-z][a-z0-9]*)\b[^>]*>/i.test(value);
}

export type ContentFormat = "html" | "markdown" | "plaintext";

/**
 * The sync payload carries no declared content type, so the format is inferred:
 * real tags → html; Markdown structure (headings, emphasis, lists, links,
 * fences, quotes) → markdown; anything else → plaintext. Order matters — HTML
 * wins because Markdown documents legally embed inline HTML, not vice versa.
 */
export function detectFormat(value: string): ContentFormat {
    if (looksLikeHtml(value)) return "html";
    if (
        /^#{1,6}\s+\S/m.test(value) ||
        /\*\*[^*\n]+\*\*/.test(value) ||
        /^\s*[-*+]\s+\S/m.test(value) ||
        /^\s*\d+\.\s+\S/m.test(value) ||
        /\[[^\]\n]+\]\([^)\n]+\)/.test(value) ||
        /^```/m.test(value) ||
        /^>\s+\S/m.test(value)
    ) return "markdown";
    return "plaintext";
}

/** Convert a side to HTML by its detected format so both panes render — and
 *  diff — through the same type stack. Output is NOT yet sanitized; every
 *  consumer goes through `diffRendered`/`sanitizeHtml`. */
export function toRenderableHtml(value: string, format: ContentFormat): string {
    if (format === "html") return value;
    if (format === "markdown") return marked.parse(value, { gfm: true, async: false }) as string;
    // plaintext: escape and preserve line structure
    const escaped = value
        .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    return `<p>${escaped.replace(/\n{2,}/g, "</p><p>").replace(/\n/g, "<br>")}</p>`;
}

interface Token {
    start: number; // offset into the concatenated text-node projection
    end: number;
    text: string;
}

function tokenize(projection: string): Token[] {
    const tokens: Token[] = [];
    const re = /\S+/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(projection)) !== null) {
        tokens.push({ start: m.index, end: m.index + m[0].length, text: m[0] });
    }
    return tokens;
}

/** Longest-common-subsequence keep-flags over two token streams. */
function lcsKeepFlags(a: string[], b: string[]): { keepA: boolean[]; keepB: boolean[] } {
    const n = a.length, m = b.length;
    // dp in a flat typed array to keep 2000x2000 tractable
    const width = m + 1;
    const dp = new Uint32Array((n + 1) * width);
    for (let i = n - 1; i >= 0; i--) {
        const row = i * width, next = (i + 1) * width;
        for (let j = m - 1; j >= 0; j--) {
            dp[row + j] = a[i] === b[j]
                ? dp[next + j + 1] + 1
                : Math.max(dp[next + j], dp[row + j + 1]);
        }
    }
    const keepA = new Array<boolean>(n).fill(false);
    const keepB = new Array<boolean>(m).fill(false);
    let i = 0, j = 0;
    while (i < n && j < m) {
        if (a[i] === b[j]) {
            keepA[i] = true;
            keepB[j] = true;
            i++; j++;
        } else if (dp[(i + 1) * width + j] >= dp[i * width + j + 1]) {
            i++;
        } else {
            j++;
        }
    }
    return { keepA, keepB };
}

/** Merge consecutive non-kept tokens into [start,end) ranges over the projection. */
function changedRanges(tokens: Token[], keep: boolean[]): Array<[number, number]> {
    const ranges: Array<[number, number]> = [];
    let open: [number, number] | null = null;
    for (let k = 0; k < tokens.length; k++) {
        if (keep[k]) {
            if (open) { ranges.push(open); open = null; }
        } else {
            if (open) open[1] = tokens[k].end;
            else open = [tokens[k].start, tokens[k].end];
        }
    }
    if (open) ranges.push(open);
    return ranges;
}

export interface DiffResult {
    /** Sanitized HTML with `<mark>` wrapping on incoming (theirs) changes. */
    theirsHtml: string;
    /** Sanitized HTML with struck `<mark>`s where a passage was deleted in theirs. */
    oursHtml: string;
    /** Number of changed runs across both sides; null when the diff was skipped. */
    runCount: number | null;
}

/** Elements whose boundaries must break tokens — `</h1><p>` carries no
 *  whitespace, so without a virtual separator "Title" + "same" would glue into
 *  one token and misalign against a source (e.g. converted Markdown) that has
 *  newlines between blocks. */
const BLOCK_TAGS = new Set([
    "H1", "H2", "H3", "H4", "P", "LI", "UL", "OL", "TR", "TD", "TH", "TABLE",
    "PRE", "BLOCKQUOTE", "DIV", "BR", "HR",
]);

interface Projection {
    /** Text-node data concatenated, with a virtual "\n" at block boundaries. */
    text: string;
    /** projection index -> offset in the pure text-node stream (-1 = virtual). */
    map: Int32Array;
}

function textProjection(root: HTMLElement): Projection {
    // The wrap pass counts only text-node characters, so virtual separators
    // must not shift offsets — the map carries the real offset per char.
    let text = "";
    const offsets: number[] = [];
    let real = 0;
    const walk = (node: Node) => {
        if (node.nodeType === Node.TEXT_NODE) {
            const data = (node as Text).data;
            for (let i = 0; i < data.length; i++) offsets.push(real + i);
            text += data;
            real += data.length;
            return;
        }
        const isBlock = node.nodeType === Node.ELEMENT_NODE &&
            BLOCK_TAGS.has((node as Element).tagName);
        if (isBlock) { text += "\n"; offsets.push(-1); }
        node.childNodes.forEach(walk);
        if (isBlock) { text += "\n"; offsets.push(-1); }
    };
    walk(root);
    return { text, map: Int32Array.from(offsets) };
}

/** Translate token ranges from projection coordinates to text-node offsets.
 *  Tokens are \S+ runs and separators are whitespace, so a token never
 *  contains a virtual char — its endpoints always map to real offsets. */
function toRealRanges(ranges: Array<[number, number]>, map: Int32Array): Array<[number, number]> {
    return ranges.map(([s, e]) => [map[s], map[e - 1] + 1] as [number, number])
        .filter(([s, e]) => s >= 0 && e > s);
}

/** Wrap [start,end) projection ranges of `root`'s text nodes in styled `<mark>`s. */
function wrapRanges(root: HTMLElement, ranges: Array<[number, number]>, className: string): void {
    if (!ranges.length) return;
    let offset = 0;
    let rangeIdx = 0;
    const doc = root.ownerDocument;

    const visit = (node: Node): void => {
        if (rangeIdx >= ranges.length) return;
        if (node.nodeType !== Node.TEXT_NODE) {
            // childNodes mutates as we split/wrap; snapshot first
            [...node.childNodes].forEach(visit);
            return;
        }
        const text = node as Text;
        const nodeStart = offset;
        const nodeEnd = offset + text.data.length;
        offset = nodeEnd;

        let current: Text | null = text;
        let currentStart = nodeStart;
        while (current && rangeIdx < ranges.length) {
            const [rs, re] = ranges[rangeIdx];
            if (re <= currentStart) { rangeIdx++; continue; }
            if (rs >= nodeEnd) break;
            const sliceStart = Math.max(rs, currentStart);
            const sliceEnd = Math.min(re, nodeEnd);
            // split off leading unmarked text
            if (sliceStart > currentStart) {
                current = current.splitText(sliceStart - currentStart);
                currentStart = sliceStart;
            }
            const marked: Text = current;
            let tail: Text | null = null;
            if (sliceEnd < nodeEnd) {
                tail = marked.splitText(sliceEnd - currentStart);
            }
            const mark = doc.createElement("mark");
            mark.className = className;
            marked.parentNode?.replaceChild(mark, marked);
            mark.appendChild(marked);
            // screen-reader annotation so the diff is not color-only (§11)
            const sr = doc.createElement("span");
            sr.className = "sr-only";
            sr.textContent = " (changed)";
            mark.appendChild(sr);
            currentStart = sliceEnd;
            if (re <= nodeEnd) rangeIdx++;
            current = tail;
        }
    };
    visit(root);
}

export const MARK_THEIRS_CLASS = "cr-mark-theirs";
export const MARK_OURS_CLASS = "cr-mark-ours";

/**
 * Sanitize both versions and highlight word-level differences (§6):
 * incoming changes marked in theirs; passages deleted in theirs struck in ours.
 * Degrades quietly — oversized or failing input renders unmarked with
 * `runCount: null`.
 */
export function diffRendered(oursRaw: string, theirsRaw: string): DiffResult {
    const oursSafe = sanitizeHtml(oursRaw);
    const theirsSafe = sanitizeHtml(theirsRaw);
    const fallback: DiffResult = { theirsHtml: theirsSafe, oursHtml: oursSafe, runCount: null };
    try {
        if (oursSafe.length > DIFF_CHAR_BUDGET || theirsSafe.length > DIFF_CHAR_BUDGET) {
            return fallback;
        }
        const oursRoot = document.createElement("div");
        oursRoot.innerHTML = oursSafe;
        const theirsRoot = document.createElement("div");
        theirsRoot.innerHTML = theirsSafe;

        const oursProj = textProjection(oursRoot);
        const theirsProj = textProjection(theirsRoot);
        const oursTokens = tokenize(oursProj.text);
        const theirsTokens = tokenize(theirsProj.text);
        if (oursTokens.length > DIFF_TOKEN_BUDGET || theirsTokens.length > DIFF_TOKEN_BUDGET) {
            return fallback;
        }
        const { keepA, keepB } = lcsKeepFlags(
            oursTokens.map(t => t.text), theirsTokens.map(t => t.text));

        const theirsRanges = changedRanges(theirsTokens, keepB);
        const oursRanges = changedRanges(oursTokens, keepA);
        wrapRanges(theirsRoot, toRealRanges(theirsRanges, theirsProj.map), MARK_THEIRS_CLASS);
        wrapRanges(oursRoot, toRealRanges(oursRanges, oursProj.map), MARK_OURS_CLASS);

        return {
            theirsHtml: theirsRoot.innerHTML,
            oursHtml: oursRoot.innerHTML,
            runCount: theirsRanges.length + oursRanges.length,
        };
    } catch {
        return fallback;
    }
}
